import React, { useEffect, useState, useMemo } from 'react';
import { formatFunctionValueStatus, Functionx, LogEntry } from '@iotopen/node-lynx';
import { useGlobalLynxClient } from '@iotopen/react-lynx';
import { SignalStrengthIndicator } from './SignalStrengthIndicator';

interface DeviceFunctionsProps {
    functions: Functionx[]
}

export const DeviceFunctions: React.FC<DeviceFunctionsProps> = ({ functions }) => {
    const {lynxClient} = useGlobalLynxClient();
    const [status, setStatus] = useState<LogEntry[] | undefined>();
    
    // Always call hooks at the top level
    const topicFilter = useMemo(() => functions.map(func => func.meta.topic_read), [functions]);
    
    // Memoize function finding at the top level
    const { rssiFunction, snrFunction, spreadFactorFunction } = useMemo(() => {
        if (!functions.length) {
            return { rssiFunction: undefined, snrFunction: undefined, spreadFactorFunction: undefined };
        }
        return {
            rssiFunction: functions.find(func => func.type === 'gateway_rssi'),
            snrFunction: functions.find(func => func.type === 'gateway_snr'),
            spreadFactorFunction: functions.find(func => func.type === 'spread_factor')
        };
    }, [functions]);

    useEffect(() => {
        const installationId = functions[0]?.installation_id;
        if(installationId){
            lynxClient.getStatus(installationId, topicFilter)
                .then(res => {
                    setStatus(res);
                })
                .catch(err => console.error('Error fetching status:', err));
        }
    }, [functions, lynxClient, topicFilter]);

    if(!functions.length) return null;
    
    let rssiValue = null;
    let snrValue = null;
    let spreadFactorValue = null;

    const getLastUpdate = () => {
        if(!status?.length) return null;
        const latest = status?.reduce((latest, currentStatus) => currentStatus.timestamp > latest.timestamp ? currentStatus : latest).timestamp;
        if (latest){
            return new Date(latest * 1000).toLocaleString();
        }
        return null;
    };

    const lastUpdate = getLastUpdate();

    if (status && rssiFunction) {
        const rssiStatus = status.find(stat => stat.topic === rssiFunction.meta.topic_read);
        if (rssiStatus) {
            rssiValue = formatFunctionValueStatus({[rssiFunction.meta.topic_read]: rssiStatus}, rssiFunction);
        }
    }

    if (status && snrFunction) {
        const snrStatus = status.find(stat => stat.topic === snrFunction.meta.topic_read);
        if (snrStatus) {
            snrValue = formatFunctionValueStatus({[snrFunction.meta.topic_read]: snrStatus}, snrFunction);
        }
    }

    if (status && spreadFactorFunction) {
        const spreadFactorStatus = status.find(stat => stat.topic === spreadFactorFunction.meta.topic_read);
        if (spreadFactorStatus) {
            spreadFactorValue = formatFunctionValueStatus({[spreadFactorFunction.meta.topic_read]: spreadFactorStatus}, spreadFactorFunction);
        }
    }

    // Convert rssiValue and snrValue to numbers if they are strings
    const rssiNumericValue = rssiValue !== null ? parseFloat(rssiValue as string) : null;
    const snrNumericValue = snrValue !== null ? parseFloat(snrValue as string) : null;

    // Use fixed style to prevent resize issues
    const textStyle = { fontSize: '0.9rem', width: 'auto' };

    return(
        <div className='d-flex flex-column' style={{ width: '100%' }}>
            {lastUpdate && 
            <div className='text-muted d-flex gap-3' style={textStyle}>
                <strong>Last Updated:</strong> {lastUpdate}
            </div>}
            <div className='text-muted d-flex flex-wrap gap-3 align-items-center' style={textStyle}>
                {rssiValue && <div style={{ minWidth: '70px' }}><strong>RSSI:</strong> {rssiValue}</div>}
                {snrValue && <div style={{ minWidth: '70px' }}><strong>SNR:</strong> {snrValue}</div>}
                {spreadFactorValue && <div><strong>Spread Factor:</strong> {spreadFactorValue}</div>}
                {rssiNumericValue !== null && snrNumericValue !== null && (
                    <div className="d-flex align-items-center gap-2">
                        <strong>Radio Performance:</strong>
                        <SignalStrengthIndicator rssiValue={rssiNumericValue} snrValue={snrNumericValue} />
                    </div>
                )}
            </div>
        </div>
    );
};